import T from 'T'

import type { ContainerOrigin, PlaceholderKind } from 'types/entities/shared'

// Narrow structural shape, not the canonical entity — types/entities bans optional fields and
// the full Container shape isn't modeled yet. Consumers intersect this with what else they read.
export interface PlacementCandidate {
  placeholder?: boolean | null
  placeholderType?: PlaceholderKind | null
  customerOwned?: boolean | null
}

export interface CoordinateCandidate extends PlacementCandidate {
  latitude?: number | string | null
  longitude?: number | string | null
}

export interface PlaceholderContainerCandidate {
  name?: string | null
  containerType?: { id?: string | null } | null
  serialNumber?: string | null
  rfId?: string | null
  qrCode?: string | null
  barCode?: string | null
}

const PLACEHOLDER_NAME_PATTERN = /^Container #\d+$/
const IDENTITY_FIELDS = ['serialNumber', 'rfId', 'qrCode', 'barCode'] as const

const hasValue = (value?: string | null) => Boolean(value && value.trim())

// Publishing a draft service mints containers carrying only a name, measure and coordinates, and
// nothing marks them as placeholders — so match that shape: positional name, no type, no identity.
export const isPlaceholderContainer = (container: PlaceholderContainerCandidate) => {
  if (!PLACEHOLDER_NAME_PATTERN.test((container?.name ?? '').trim())) return false
  if (container.containerType?.id) return false

  return !IDENTITY_FIELDS.some(field => hasValue(container[field]))
}

// Deliberately narrower than isPlaceholderContainer above, and not a replacement for it yet: the
// positional name is what the add-container form fills in by default, so guessing off it flags a
// container a CSR made by hand. Until the backend sets the flag these read false everywhere, and
// every treatment keyed off them stays dormant.
export const isPlacement = (container: PlacementCandidate) => container?.placeholder === true

// The billing-only row: it holds the service's slot and is never going to be filled.
export const isNoContainerPlacement = (container: PlacementCandidate) =>
  isPlacement(container) && container.placeholderType === 'NONE_EXPECTED'

// A delivery someone is waiting on. An absent kind means pending.
export const isPendingPlacement = (container: PlacementCandidate) => isPlacement(container) && !isNoContainerPlacement(container)

export interface ServiceLinkedCandidate extends PlacementCandidate {
  services?: string[] | null
}

// Both placement verbs act through the service that minted the row — assigning fills that service's
// spot, cancelling settles what it is left with — so a row carrying no service can take neither.
export const canActOnPlacement = (container: ServiceLinkedCandidate) =>
  isPendingPlacement(container) && (container?.services?.length ?? 0) > 0

const formatCoordinate = (value: CoordinateCandidate['latitude']) => (typeof value === 'number' ? value.toFixed(5) : value || '—')

// One formatter for every surface that prints a row's coordinates, so the card and the Map & Routing
// panel cannot drift: a missing coordinate is a dash, never 0, and a pending row leads with Expected.
export const formatCoordinates = (row: CoordinateCandidate) => {
  if (isNoContainerPlacement(row)) return ''
  const coordinates = `${formatCoordinate(row.latitude)}, ${formatCoordinate(row.longitude)}`
  return isPendingPlacement(row) ? `${T.EXPECTED} · ${coordinates}` : coordinates
}

// Crosscuts isPlacement: the customer's bin can be on site or not recorded yet, so a row may be both.
export const isCustomerOwned = (container: PlacementCandidate) => container?.customerOwned === true

// The one predicate every count, badge and export should use. Counting either kind as inventory is
// what inflates the number a hauler buys against.
export const isInventoryAsset = (container: PlacementCandidate) => !isPlacement(container) && !isCustomerOwned(container)

// Real containers and pending placements are selectable; no-container rows are service-level facts.
export const isBulkSelectable = (container: PlacementCandidate) => !isPlacement(container) || isPendingPlacement(container)

export interface ContainerRowSummary {
  total: number
  pending: number
  noContainer: number
  customerOwned: number
}

// Customer-owned counts real rows only: a customer-owned placement is already in `pending`.
export const summarizeContainerRows = (containers: PlacementCandidate[]): ContainerRowSummary => ({
  total: containers.length,
  pending: containers.filter(isPendingPlacement).length,
  noContainer: containers.filter(isNoContainerPlacement).length,
  customerOwned: containers.filter(container => isCustomerOwned(container) && !isPlacement(container)).length,
})

/** What the caller decided to do with the row it is removing. */
export type RemoveOutcome = 'remove' | 'keep-spot' | 'no-container' | 'deactivate'

/** The three answers to "this is the last row" — `remove` is the case that asks the question. */
export type LastRowChoice = Exclude<RemoveOutcome, 'remove'>

/** A settled outcome. `deactivate` never reaches the rule; the caller routes it to the service. */
export type SettledOutcome = Exclude<RemoveOutcome, 'deactivate'>

export interface RemovableRow extends PlacementCandidate {
  id: string
  origin?: ContainerOrigin | null
  serialNumber?: string | null
}

// Ownership goes with the serial on both answers: it described the container that left, and what
// arrives in its place is far more often one of the hauler's own.
const asPendingRow = <Row extends RemovableRow>(row: Row): Row => ({
  ...row,
  origin: 'SYSTEM',
  placeholder: true,
  placeholderType: 'PENDING',
  customerOwned: null,
  serialNumber: null,
})

const asNoContainerRow = <Row extends RemovableRow>(row: Row): Row => ({
  ...row,
  origin: 'SYSTEM',
  placeholder: true,
  placeholderType: 'NONE_EXPECTED',
  customerOwned: null,
  serialNumber: null,
})

/**
 * Whether removing this many rows at once would leave a live service with nothing on site — the
 * single-row fork's invariant, asked of a selection. A deactivated service may reach zero.
 */
export const wouldEmptyService = (selectedCount: number, totalRows: number, isServiceDeactivated: boolean): boolean =>
  !isServiceDeactivated && totalRows > 0 && selectedCount >= totalRows

// THE outcome rule. Every surface that removes a row from a configured service calls this one
// function — the Containers accordion, its row menu, and the Map & routing panel — so none of them
// can empty a live service on its own. Only `remove` drops a row; the other two rewrite it in
// place, keeping its id and location, so the service's row count is unchanged.
export const containersAfterRemove = <Row extends RemovableRow>(rows: Row[], id: string, outcome: SettledOutcome): Row[] => {
  if (outcome === 'remove') return rows.filter(row => row.id !== id)

  return rows.map(row => {
    if (row.id !== id) return row
    return outcome === 'keep-spot' ? asPendingRow(row) : asNoContainerRow(row)
  })
}

// Shares the rule's row shapes rather than restating them. `no-container` collapses to exactly ONE
// row: it is a statement about the service, and N copies of it say the same thing N times. The
// survivor is an existing selected row, so the statement keeps an id and a location. `keep-spot`
// holds one spot per vacated location; selected placements are cancelled, not held.
export const containersAfterBulkRemove = <Row extends RemovableRow>(
  rows: Row[],
  selectedIds: Set<string>,
  outcome: Exclude<LastRowChoice, 'deactivate'>
): Row[] => {
  const keepSpots = outcome === 'keep-spot'
  const anchorId = keepSpots ? undefined : rows.find(row => selectedIds.has(row.id))?.id

  return rows
    .filter(row => {
      if (!selectedIds.has(row.id)) return true
      return keepSpots ? !isPlacement(row) : row.id === anchorId
    })
    .map(row => {
      if (!selectedIds.has(row.id)) return row
      return keepSpots ? asPendingRow(row) : asNoContainerRow(row)
    })
}
